/**
 * Computes the diff between two group arrays, matched by `id`.
 *
 * @returns `added`   — items in `nextGroups` not present in `previousGroups`
 *          `removed` — items in `previousGroups` not present in `nextGroups`
 */
export function computeGroupChanges<T extends { id: string }>(
  previousGroups: T[],
  nextGroups: T[]
): { added: T[]; removed: T[] } {
  const previousIds = new Set(previousGroups.map((g) => g.id));
  const nextIds = new Set(nextGroups.map((g) => g.id));

  return {
    added: nextGroups.filter((g) => !previousIds.has(g.id)),
    removed: previousGroups.filter((g) => !nextIds.has(g.id)),
  };
}

/**
 * Computes the intersection of group names across all rows, then re-hydrates
 * the matching items from `availableItems` (matched by `name`).
 *
 * - 0 rows → empty array
 * - 1 row  → that row's groups
 * - N rows → only groups present in every row
 *
 * @param rows           The selected rows, each carrying a `groups` array
 * @param getRowGroups   Extracts the groups from a row (returns items with a `name`)
 * @param availableItems The full pool to re-hydrate matches from (must have `name`)
 */
export function computeCommonGroups<
  TRow,
  TGroup extends { name?: string | null },
  TItem extends { id: string; name: string }
>(
  rows: TRow[],
  getRowGroups: (row: TRow) => TGroup[],
  availableItems: TItem[]
): TItem[] {
  if (rows.length === 0) return [];

  const commonNames = rows.reduce<Set<string>>((acc, row, index) => {
    const rowNames = new Set(
      getRowGroups(row)
        .map((g) => g.name)
        .filter((name): name is string => !!name)
    );

    if (index === 0) {
      return rowNames;
    }

    for (const name of acc) {
      if (!rowNames.has(name)) {
        acc.delete(name);
      }
    }
    
    return acc;
  }, new Set());

  return [...commonNames]
    .map((name) => availableItems.find((item) => item.name === name))
    .filter((item): item is TItem => !!item);
}